/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useRef } from 'react';
import { getLocaleTimeOffset, WorkflowStep } from 'ogc-processes-components';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';
import './TimelineExecution.scss';

import { AntFlowJob } from '@/types/models';
import { useTimelineOptions } from './TimelineExecution/hooks/useTimelineOptions';
import { useTraceData } from './TimelineExecution/hooks/useTraceData';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { useVisTimeline } from './TimelineExecution/hooks/useVisTimeline';

interface Props {
  className?: string;
  job: AntFlowJob;
  workflowSteps?: Array<WorkflowStep> | Record<string, WorkflowStep>;
  setSelectedLogId: (url: string | undefined) => void;
}

export const TimelineExecution: React.FC<Props> = ({ job, className, workflowSteps, setSelectedLogId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offsetMinute] = getLocaleTimeOffset(job.created ? new Date(job.created) : undefined);

  const { timelineOptions } = useTimelineOptions({ job, offsetMinute });
  const { executionTrace, items, groups } = useTraceData({ job, workflowSteps, offsetMinute });
  useVisTimeline({
    containerRef,
    items,
    groups,
    timelineOptions,
    setSelectedLogId
  });

  const isAwaitingStart = executionTrace.every((step) => !step.started);

  return (
    <div className={classNames('timeline-execution-container', className)}>
      <>
        <div ref={containerRef} className="timeline-container"></div>
        {isAwaitingStart && (
          <div
            style={{
              height: containerRef.current?.clientHeight ?? '20px'
            }}
            className="awaiting-start"
          >
            <ProgressSpinner
              style={{ width: '50px', height: '50px' }}
              strokeWidth="5"
              fill="var(--surface-ground)"
              animationDuration=".5s"
            />
          </div>
        )}
      </>
    </div>
  );
};
